<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  deal: { type: Object, required: true },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(['click', 'select']);

const toggleSelect = checked => emit('select', checked);

const handleClick = () => {
  if (props.selectable) {
    toggleSelect(!props.isSelected);
    return;
  }
  emit('click', props.deal);
};

const { t } = useI18n();

const getAgentById = useMapGetter('agents/getAgentById');

const assignee = computed(() => {
  if (!props.deal.assignedToId) return null;
  const agent = getAgentById.value(props.deal.assignedToId);
  return agent?.id ? agent : (props.deal.assignedTo ?? null);
});

const assigneeTooltip = computed(() => {
  if (!assignee.value) return '';
  const name = assignee.value.name || assignee.value.email;
  return assignee.value.active === false
    ? `${name} ${t('DEALS.DEACTIVATED_AGENT_SUFFIX')}`
    : name;
});

const formattedValue = computed(() => {
  if (!props.deal.value) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.deal.currency || 'BRL',
  }).format(props.deal.value);
});
</script>

<template>
  <div
    class="flex items-start gap-2 p-3 rounded-xl border border-n-weak bg-n-solid-1 hover:bg-n-alpha-2 cursor-pointer transition-colors shadow-sm"
    :class="{
      'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
    }"
    @click="handleClick"
  >
    <Checkbox
      v-if="selectable"
      :model-value="isSelected"
      class="shrink-0 mt-0.5"
      @change="event => toggleSelect(event.target.checked)"
      @click.stop
    />
    <div class="flex flex-col gap-2 flex-1 min-w-0">
      <span class="text-sm font-medium text-n-slate-12 leading-snug">
        {{ deal.name || t('DEALS.EMPTY_STATE.TITLE') }}
      </span>
      <span v-if="formattedValue" class="text-xs font-semibold text-green-700">
        {{ formattedValue }}
      </span>
      <div v-if="deal.labels?.length" class="flex flex-wrap gap-1">
        <span
          v-for="label in deal.labels"
          :key="label.title"
          class="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full border border-n-weak text-n-slate-11"
        >
          <span
            class="size-1.5 rounded-full shrink-0"
            :style="{ backgroundColor: label.color }"
          />
          {{ label.title }}
        </span>
      </div>
    </div>
    <Avatar
      v-if="assignee"
      v-tooltip.top="{
        content: assigneeTooltip,
        delay: { show: 500, hide: 0 },
      }"
      :name="assignee.name || assignee.email"
      :src="assignee.thumbnail"
      :size="24"
      rounded-full
      class="flex-shrink-0"
    />
  </div>
</template>
